import { rd, type RemoteData } from '@passionware/monads';
import { ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ImagePreview } from 'v5/design-system/ImagePreview';
import { MediaMoreOverlay } from 'v5/design-system/MediaBadges';
import { Skeleton } from 'v5/design-system/Skeleton';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import { resolvePinMedia } from 'v5/patterns/widgets/MediaCarousel/mediaCarousel.resolver';
import type { MediaPin } from 'v5/patterns/widgets/MediaCarousel/mediaCarousel.types';

const SHOWN = 5;

/**
 * A resource's pictures in a hover card: up to five squares, the last saying how many more. Always
 * the same height, loading or empty too, so the card doesn't grow once open. A square opens the
 * pictures (`onOpen`).
 */
export function ResourcePinStrip({
  pins,
  onOpen
}: {
  pins: RemoteData<MediaPin[]>;
  onOpen: () => void;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  return (
    <div className="flex h-14 items-center gap-1.5 border-t border-border-default px-4">
      {rd
        .journey(pins)
        .wait(
          Array.from({ length: SHOWN }, (_, index) => (
            <Skeleton key={index} className="size-11 rounded-md" />
          ))
        )
        .catch(() => (
          <span className="text-sm text-fg-muted">{t('picturesFailed')}</span>
        ))
        .map(all => {
          const shown = all.flatMap(pin => {
            const media = resolvePinMedia(pin);
            return media ? [{ pin, src: media.src }] : [];
          });
          if (shown.length === 0) {
            return (
              <>
                <div className="grid size-11 place-items-center rounded-md bg-bg-subtle text-fg-muted">
                  <ImageOff className="size-4" aria-hidden />
                </div>
                <span className="text-sm text-fg-muted">{t('noPictures')}</span>
              </>
            );
          }
          const more = shown.length - SHOWN;
          const moreLabel = t('morePicturesLabel', { count: more });
          return shown.slice(0, SHOWN).map(({ pin, src }, index) => (
            <button
              key={pin.id}
              type="button"
              aria-label={
                index === SHOWN - 1 && more > 0
                  ? `${t('openPictures')}, ${moreLabel}`
                  : t('openPictures')
              }
              onClick={onOpen}
              className={cn(
                'relative size-11 shrink-0 overflow-hidden rounded-md',
                focusRings.focus.visible
              )}
            >
              <ImagePreview src={src} alt="" noUpscale className="size-full" />
              {index === SHOWN - 1 && (
                <MediaMoreOverlay count={more} label={moreLabel} />
              )}
            </button>
          ));
        })}
    </div>
  );
}
